import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import {
  INTERIOR_PAGE_CONTAINER,
  MOBILE_BOTTOM_NAV_SPACER,
  SWAP_PAGE_CONTAINER,
} from '@/config/layoutBreakpoints'
import {
  isMinimalChromePath,
  isMobileBrowseShellRoute,
  isNutrimaticOperationalRoute,
} from '@/config/mobileRoutes'
import { WorkspacePreferencesProvider } from '@/features/workspace/useWorkspacePreferences'
import { useMobileShellMedia } from '@/hooks/useMobileShellMedia'
import { cn } from '@/lib/utils'
import { MobileAppHeader } from '@/mobile/platform/MobileAppHeader'
import { MobileBrowseTitleProvider } from '@/mobile/platform/MobileBrowseTitleContext'
import { MobileShellMenuProvider } from '@/mobile/platform/MobileShellMenuContext'
import { MobileTokenSearchProvider } from '@/mobile/token-picker/MobileTokenSearchProvider'
import { stabilizeMobileViewportLayout } from '@/mobile/platform/mobileViewportMetrics'
import { usePlatformMobileScrollLock } from '@/mobile/platform/usePlatformMobileScrollLock'

import { AppHeader } from './AppHeader'
import { AppMobileBottomNav } from './AppMobileBottomNav'
import { AppSidebar } from './AppSidebar'

function isHomePath(pathname: string) {
  return pathname === '/'
}

function isNutrimaticMobileLayoutPath(pathname: string) {
  return isNutrimaticOperationalRoute(pathname)
}

function DesktopShellHeader() {
  return (
    <div className="hidden shrink-0 md:block">
      <AppHeader />
    </div>
  )
}

function resetShellScroll(main: HTMLElement | null) {
  main?.scrollTo(0, 0)
  window.scrollTo(0, 0)
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0
}

function PlatformMobileChrome({
  children,
  mobileNavOpen,
  onToggleMenu,
  onOpenMenu,
  onCloseMenu,
  pathname,
  nutrimaticRoute,
  mainRef,
}: {
  children: React.ReactNode
  mobileNavOpen: boolean
  onToggleMenu: () => void
  onOpenMenu: () => void
  onCloseMenu: () => void
  pathname: string
  nutrimaticRoute: boolean
  mainRef: React.RefObject<HTMLElement | null>
}) {
  const isMobileShell = useMobileShellMedia()
  const browseMobile = isMobileShell && isMobileBrowseShellRoute(pathname)
  usePlatformMobileScrollLock(browseMobile)

  const hideBottomNav = isNutrimaticOperationalRoute(pathname)
  const showMobileBottomNav = isMobileShell && !hideBottomNav
  const needsBottomNavSpacer = showMobileBottomNav && !nutrimaticRoute

  return (
    <>
      <MobileBrowseTitleProvider pathname={pathname}>
        <MobileShellMenuProvider openMenu={onOpenMenu}>
          <div className={cn('flex min-h-0 flex-1 flex-col overflow-hidden', nutrimaticRoute && 'max-lg:overscroll-none')}>
            {browseMobile ? <MobileAppHeader onMenuClick={onToggleMenu} /> : null}
            <div className="flex min-h-0 flex-1 overflow-hidden">
              <AppSidebar mobileOpen={mobileNavOpen} onClose={onCloseMenu} />
              <main
            ref={mainRef}
            data-mobile-scroll-container={browseMobile ? true : undefined}
            className={cn(
              'flex min-h-0 flex-1 flex-col md:overflow-y-auto md:pb-0',
              nutrimaticRoute
                ? 'max-lg:overflow-hidden max-lg:pb-0'
                : browseMobile
                  ? cn(
                      'overflow-y-auto overscroll-contain [touch-action:pan-y] [-webkit-overflow-scrolling:touch]',
                    )
                  : 'overflow-y-auto pb-[env(safe-area-inset-bottom)]',
            )}
          >
            <div
              className={cn(
                nutrimaticRoute ? SWAP_PAGE_CONTAINER : INTERIOR_PAGE_CONTAINER,
                nutrimaticRoute &&
                  'max-lg:flex max-lg:min-h-0 max-lg:flex-1 max-lg:flex-col max-lg:overflow-hidden max-lg:overscroll-none max-lg:bg-[var(--mobile-surface,#0f1219)]',
                browseMobile &&
                  'max-md:mx-0 max-md:max-w-none max-md:px-4 max-md:pt-3 max-md:pb-0 max-md:min-h-0',
                !nutrimaticRoute && !browseMobile && 'max-md:min-h-[calc(100dvh-4rem)] max-md:pb-0',
              )}
              data-layout="platform-shell"
              data-route={
                isHomePath(pathname) ? 'home' : nutrimaticRoute ? 'nutrimatic' : 'interior'
              }
              data-variant={isHomePath(pathname) ? 'home' : nutrimaticRoute ? 'nutrimatic' : 'standard'}
            >
              {children}
              {needsBottomNavSpacer ? (
                <div className={cn(MOBILE_BOTTOM_NAV_SPACER, 'max-md:block hidden')} aria-hidden />
              ) : null}
            </div>
          </main>
            </div>
          </div>
        </MobileShellMenuProvider>
      </MobileBrowseTitleProvider>
      <AppMobileBottomNav hiddenOnMobile={hideBottomNav} />
    </>
  )
}

export function PlatformShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  const minimal = isMinimalChromePath(pathname)
  const nutrimaticRoute = isNutrimaticMobileLayoutPath(pathname)
  const mainRef = useRef<HTMLElement>(null)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  useEffect(() => {
    stabilizeMobileViewportLayout()
    resetShellScroll(mainRef.current)
  }, [pathname])

  useEffect(() => {
    if (!nutrimaticRoute) return
    resetShellScroll(mainRef.current)
  }, [nutrimaticRoute])

  return (
    <WorkspacePreferencesProvider>
      {minimal ? (
        <div className="relative z-10 flex h-[100dvh] flex-col overflow-hidden">
          <DesktopShellHeader />
          <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
        </div>
      ) : (
        <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden">
          <DesktopShellHeader />
          <MobileTokenSearchProvider>
            <PlatformMobileChrome
              pathname={pathname}
              nutrimaticRoute={nutrimaticRoute}
              mainRef={mainRef}
              mobileNavOpen={mobileNavOpen}
              onToggleMenu={() => setMobileNavOpen((open) => !open)}
              onOpenMenu={() => setMobileNavOpen(true)}
              onCloseMenu={() => setMobileNavOpen(false)}
            >
              {children}
            </PlatformMobileChrome>
          </MobileTokenSearchProvider>
        </div>
      )}
    </WorkspacePreferencesProvider>
  )
}
